import { landmineNames } from '@mm/constants/landmine-names';
import adAccountCRUDActions from '@mm/state/actions/AdAccountCRUDActions';
import audiencesCRUDActions from '@mm/state/actions/AudiencesCRUDActions';
import { usePageCRUDActions } from '@mm/state/actions/PageCRUDActions';
import sponsoredMessageCRUDActions from '@mm/state/actions/SponsoredMessageCRUDActions';
import { useWidgetCRUDActions } from '@mm/state/actions/WidgetCRUDActions';
import { AdSetBudgetType } from '@mm/state/interfaces/adAccounts';
import AdAccountModel from '@mm/state/model/AdAccountModel';
import { MMTargeting } from '@mm/state/model/AdConfigModel';
import {
  ExtendedSponsoredMessageModel,
  SponsoredMessageModel,
} from '@mm/state/model/SponsoredMessageModel';
import WidgetModel from '@mm/state/model/WidgetModel';
import modalActions, {
  useModalActions,
} from '@mm/state/modules/modalsState/actions';
import {
  activeFacebookPagePlanInfoSelector,
  activeFacebookPageSelector,
} from '@mm/state/selectors/facebookPageSelector';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { selectAudiences } from '@mm/state/selectors/messengerAdSelectors';
import { pageEntityByIdSelector } from '@mm/state/selectors/pageSelectors';
import {
  activeChatbotIdSelector,
  activeChatbotSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import { widgetsByDialogueSelector } from '@mm/state/selectors/widgetSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { useMachine } from '@xstate/react';
import { isArray, isEqual, omitBy, unionBy } from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { Label } from 'reactstrap';
import { compose } from 'recompose';
import { Dispatch } from 'redux';
import useRouter from 'use-react-router';
import { Machine } from 'xstate';
import Button from '../../../../../common/buttons/DeprecatedButton/DeprecatedButton';
import { AccountErrorTooltip } from '../../../../../common/components/AccountErrorTooltip/AccountErrorTooltip';
import Datetime from '../../../../../common/components/DateTimePicker/DateTimePicker';
import { ErrorTooltip } from '../../../../../common/components/ErrorTooltip/ErrorTooltip';
import {
  generateBasicStructure,
  nameById,
} from '@mm/state/helpers/filterable-select-helpers';
import FilterableSelect from '../../../../../common/components/FilterableSelect/FilterableSelect';
import Hint from '../../../../../common/components/Hint/Hint';
import ImageUploader from '../../../../../common/components/ImageUploader/ImageUploader';
import { InputWithEmoji } from '../../../../../common/components/InputWithEmoji/InputWithEmoji';
import { PrimaryToolTip } from '../../../../../common/components/PrimaryToolTip/PrimaryToolTip';
import SelectorWithArrow from '../../../../../common/components/SelectorWithArrow/SelectorWithArrow';
import TextInput from '../../../../../common/components/TextInput';
import { TooltipContainer } from '../../../../../common/components/TooltipContainer/TooltipContainer';
import { RouterComponentProps } from '../../../../../common/helpers/routes/interfaces';
import { addNamesToAccounts } from '../../../../../common/helpers/utils';
import { useBotChannels } from '../../../../../common/hooks/use-bot-channels';
import { useFormSteps } from '../../../../../common/hooks/use-form-steps';
import { usePrevious } from '../../../../../common/hooks/use-previous';
import {
  PathGraphContext,
  useNewGraphInstance,
} from '../../../../../common/widgets/common/PathGraph/PathGraph';
import { BotBuilderRoutes } from '../../../../../routes/bot-builder-routes';
import { DialogueSettingsButton } from '../../../../common/DialogueSettingsButton/DialogueSettingsButton';
import { useInactiveBotActivator } from '../../../../common/hooks/use-inactive-bot-activator';
import { DEFAULT_CURRENCY, symbolForCurrency } from '../../../../common/utils';
import { InactiveBotAlert } from '../../../../components/InactiveBotAlert/InactiveBotAlert';
import { AudienceSelector } from '../../../campaigns/components/AudienceSelector/AudienceSelector';
import { formStepContainerBuilder } from '../../../clickToMessengerAds/components/AdConfigDetail/FormStepContainer/FormStepContainer';
import { Targeting } from '../../../clickToMessengerAds/components/AdConfigDetail/Targeting/Targeting';
import { defaultTargeting } from '../../../clickToMessengerAds/components/AdConfigDetail/Targeting/utils';
import '../../../clickToMessengerAds/components/AdDetail.pcss';
import { November2020EuroAdsChangesAlert } from '../../../clickToMessengerAds/components/November2020EuroAdsChangesAlert/November2020EuroAdsChangesAlert';
import {
  isValidStep,
  onPublish,
} from '../../../clickToMessengerAds/utils/utils';
import { DialogueHeaderWarningAlert } from '../../../common/components/DialogueHeaderWarningAlert/DialogueHeaderWarningAlert';
import { FacebookAdsAlert } from '../../../common/components/FacebookAdsAlert/FacebookAdsAlert';
import { StartBotsDialogueEditor } from '../../../startBotPromoters/common/StartBotsDialogueEditor/StartBotsDialogueEditor';
import { StartBotSponsoredSelector } from '../../../startBotPromoters/common/StartBotSelector/StartBotSelector';
import { EditPromoBlasterHeader } from '../EditPromoBlasterHeader/EditPromoBlasterHeader';
import {
  budgets,
  canPublish,
  FullPBContext,
  hasDataFromReduxStore,
  MIN_BID_CAP,
  noneAudience,
  PBMachineConfig,
  PromoBlasterMachineEventSchema,
} from './PBStateMachine';
import './PromoBlasterDetails.pcss';

const CONTACTS_THRESHOLD = 200;

type OuterProps = {
  promoBlastId?: number;
};

type InnerProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  RouterComponentProps;

type Props = InnerProps & OuterProps;

const noPastDate = (date: Date) => moment.max(moment(date), moment());

const mapStateToProps = (state: StoreState) => ({
  chatbotId: activeChatbotIdSelector(state),
  activePageId: activeFacebookPageIdSelector(state) as number,
  adAccounts: state.adAccounts.entities,
  nextCursor: state.adAccounts.next,
  sponsoredMessages: state.sponsoredMessages.entities,
});

const Alert = ({
  isFree,
  sponsoredMessage,
}: {
  isFree?: boolean;
  sponsoredMessage?: SponsoredMessageModel;
}) => {
  if (!sponsoredMessage) {
    return null;
  }
  const needsAlert = !sponsoredMessage.draft && isFree;
  if (needsAlert) {
    return (
      <FacebookAdsAlert
        className="ad-detail__publish-alert"
        forSponsored={true}
        trialEnded={sponsoredMessage.publishedOnTrial}
      />
    );
  }
  return null;
};

const accountCurrency = (adAccount?: AdAccountModel) => {
  return adAccount?.currency || DEFAULT_CURRENCY;
};

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  prepareReplacementPage: async (
    chatbotId: number,
    sponsoredMessageId: number,
    pageId?: number,
  ) =>
    dispatch(
      sponsoredMessageCRUDActions.prepareReplacementPage(
        chatbotId,
        sponsoredMessageId,
        pageId,
      ),
    ),
  publishSponsoredMessage: (
    chatbotId: number,
    sponsoredMessageId: number,
    changes: Partial<SponsoredMessageModel>,
  ) => onPublish(dispatch, sponsoredMessageId, chatbotId, changes),
  getRemoteAccounts: (fbPageId: number | undefined, next: string = '') =>
    dispatch(adAccountCRUDActions.retrieveAll(fbPageId, false, '', true)),
  openPromoBlasterModal: async (
    publishCallback: () => any,
    cancelCallback: () => any,
  ) =>
    dispatch(
      modalActions.openPromoBlasterModal({
        publishCallback,
        cancelCallback,
      }),
    ),
  assignAdAccount: async (fbPageId: number, adAccount: AdAccountModel) =>
    dispatch(adAccountCRUDActions.assignAdAccount(fbPageId, adAccount, false)),
  getAudiences: (chatbotId: number) =>
    dispatch(audiencesCRUDActions.retrieveAll(chatbotId)),
  fetchSponsoredMessage: (chatbotId: number, sponsoredMessageId: number) =>
    dispatch(
      sponsoredMessageCRUDActions.retrieveById(chatbotId, sponsoredMessageId),
    ),
  updateMessage: (
    chatbotId: number,
    sponsoredMessageId: number,
    updates: Partial<ExtendedSponsoredMessageModel>,
  ) =>
    dispatch(
      sponsoredMessageCRUDActions.update(
        chatbotId,
        sponsoredMessageId,
        updates,
      ),
    ),
});

type RouteParams = { id: string };
type PBContextKeys = keyof FullPBContext;

const stepFields: PBContextKeys[][] = [
  ['adAccount'],
  [],
  ['bidAmount', 'budgetValue'],
  ['imageTitle'],
  [],
];

const useWidgetChanges = (
  widgets: WidgetModel[],
  prevPageWidgets: { widgets: WidgetModel[] } | undefined,
  cb: () => void,
) => {
  useEffect(() => {
    const prevWidgets = (prevPageWidgets || { widgets: [] }).widgets;
    if (!isEqual(widgets, prevWidgets) && prevWidgets.length > 0) {
      cb();
    }
  }, [widgets]);
};

// tslint:disable cyclomatic-complexity
const PromoBlasterDetailPanelBase = ({
  history,
  chatbotId,
  activePageId,
  fetchSponsoredMessage,
  prepareReplacementPage,
  publishSponsoredMessage,
  updateMessage,
  adAccounts,
  getAudiences,
  openPromoBlasterModal,
  getRemoteAccounts,
  assignAdAccount,
  sponsoredMessages,
}: Props) => {
  const {
    match: {
      params: { id },
    },
  } = useRouter<RouteParams>();

  const updateDataService = useCallback(
    async (
      ctx: FullPBContext,
      {
        data: { updates, error },
      }: {
        type: 'ENTITY_UPDATE';
        data: {
          updates: Partial<ExtendedSponsoredMessageModel>;
          error?: keyof SponsoredMessageModel;
        };
      },
    ) => {
      if (ctx.sponsoredMessage && ctx.chatbotId) {
        const result = await updateMessage(ctx.chatbotId, Number(id), updates);
        if (isArray(result)) {
          return { error: result, key: error };
        }
        return { updated: result, key: error };
      }
    },
    [],
  );

  const activeFacebookPagePlanInfo = useSelector(
    activeFacebookPagePlanInfoSelector,
  ).info;
  const featureFlags = useSelector(combinedFeatureFlagsSelector);
  const pro = !!featureFlags.adsOneSponsoredAd;
  const filteredAds = (sponsoredMessages || []).filter(a => !a.draft);
  const armLandmine =
    !pro || (!featureFlags.adsSponsored && filteredAds.length > 1);

  const modalActions = useModalActions();
  const promoPublishModalService = useCallback(async () => {
    await openPromoBlasterModal(
      () => pbSend({ type: 'CONFIRM_PUBLISHED' }),
      () => pbSend({ type: 'CONFIRM_PUBLISHED' }),
    );
    return null;
  }, []);

  const promoModalService = useCallback(async () => {
    if (armLandmine) {
      if (pro) {
        await modalActions.openUniversalUpgradeLandmineModal({
          landmineName: 'SPONSORED_MESSAGES_PRO_ACTIVATE',
          feature: 'adsSponsored',
          stayOnPage: true,
        });
      } else {
        await modalActions.openUniversalUpgradeLandmineModal({
          landmineName: landmineNames['PROMO-BLASTERS__CREATE-NEW'],
          feature: 'adsSponsored',
          stayOnPage: true,
        });
      }
      pbSend({ type: 'CANCEL_SENDING' });
    } else {
      pbSend({ type: 'CONFIRM_SENDING' });
    }
    return null;
  }, []);

  const fetchDataService = useCallback(async (ctx: FullPBContext) => {
    if (ctx.chatbotId && id) {
      let sponsoredMessage = await fetchSponsoredMessage(
        ctx.chatbotId,
        Number(id),
      );
      if (!sponsoredMessage.draft) {
        if (!sponsoredMessage.publishingProcess) {
          sponsoredMessage = await prepareReplacementPage(
            ctx.chatbotId,
            Number(id),
          );
        }
        setTouchedForms(5);
        setNextClicked(4);
      } else if (sponsoredMessage.progressStep !== null) {
        setTouchedForms(sponsoredMessage.progressStep);
        setNextClicked(sponsoredMessage.progressStep);
      }
      return sponsoredMessage;
    }
  }, []);

  const [qqPageId, setQQPageId] = useState<number | undefined>(undefined);
  const [textPageId, setTextPageId] = useState<number | undefined>(undefined);

  const qqPathGraph = useNewGraphInstance(qqPageId);
  const textPathGraph = useNewGraphInstance(textPageId);

  const qqPage = useSelector((state: StoreState) =>
    pageEntityByIdSelector(state, qqPageId?.toString() || ''),
  );
  const textPage = useSelector((state: StoreState) =>
    pageEntityByIdSelector(state, textPageId?.toString() || ''),
  );

  const publishSponsoredMessageService = useCallback((ctx: FullPBContext) => {
    const changes = omitBy(
      ctx.localUpdates,
      (value, key: keyof Partial<SponsoredMessageModel>) =>
        ctx.sponsoredMessage?.[key] === value,
    );
    if (ctx.sponsoredMessage && ctx.chatbotId) {
      return publishSponsoredMessage(
        ctx.chatbotId,
        ctx.sponsoredMessage.id,
        changes,
      );
    }
    return Promise.reject('Sponsored message is not ready for publishing');
  }, []);

  const widgetCRUDActions = useWidgetCRUDActions();
  const pageCRUDACtions = usePageCRUDActions();
  const qqWidgets = useSelector((state: StoreState) =>
    widgetsByDialogueSelector(qqPageId, state.widget.entities),
  );
  const facebookPage = useSelector(activeFacebookPageSelector);
  const session = useSelector((state: StoreState) => state.session.session);
  const textWidgets = useSelector((state: StoreState) =>
    widgetsByDialogueSelector(textPageId, state.widget.entities),
  );
  const activeBot = useSelector(activeChatbotSelector);

  // State Machine options with added/wrapped Redux
  const promoBlasterOptions = useMemo(
    () => ({
      actions: {
        redirectToDashboardAction: () => {
          const route = BotBuilderRoutes.campaigns.getLink({
            chatbotId,
            kind: 'sponsored_messages',
          });
          history.push(route);
        },
      },
      services: {
        promoModalService,
        promoPublishModalService,
        fetchDataService,
        updateDataService,
        publishSponsoredMessageService,
      },
      guards: {
        hasDataFromReduxStore,
      },
    }),
    [chatbotId],
  );

  const pbMachine = useMemo(() => {
    return Machine<FullPBContext, any, PromoBlasterMachineEventSchema>(
      PBMachineConfig,
      promoBlasterOptions,
    );
  }, [promoBlasterOptions]);

  useEffect(() => {
    if (qqPageId && qqWidgets.length === 0) {
      widgetCRUDActions.retrieveAll(qqPageId);
    }
  }, [qqPageId]);

  useEffect(() => {
    if (textPageId && textWidgets.length === 0) {
      widgetCRUDActions.retrieveAll(textPageId);
    }
  }, [textPageId]);

  const [pbState, pbSend] = useMachine(pbMachine, {
    devTools: false,
  });

  const updateSponsoredMessage = (
    update: Partial<ExtendedSponsoredMessageModel>,
    errorField?: keyof SponsoredMessageModel,
  ) =>
    pbSend({
      type: 'ENTITY_UPDATE',
      data: {
        updates: update,
        error: errorField,
      },
    });

  const allowPublishing =
    pbState?.matches('dataEntry.ready') ||
    pbState?.matches('dataEntry.published');
  const targeting = useMemo(() => {
    return pbState.context.targeting || defaultTargeting();
  }, [pbState.context.targeting]);

  const sponsoredMessage = pbState.context.sponsoredMessage;
  const [touchedForms, setTouchedForms] = useState(0);
  const [nextClicked, setNextClicked] = useState(0);
  const prevQQWidgets = usePrevious({ widgets: qqWidgets });
  const prevTextWidgets = usePrevious({ widgets: textWidgets });

  const isCollapsed = (index: number) => {
    if (index === 0) {
      return false;
    }
    return nextClicked < index;
  };

  const setTouchedFormsLevel = (level: number) => {
    setTouchedForms(Math.max(level, touchedForms));
  };

  const wrappedUpdate: <T extends (...args: any) => void>(
    level: number,
    callback: T,
  ) => (...params: Parameters<T>) => void =
    (level, callback) =>
    (...args) => {
      setTouchedFormsLevel(level);
      // @ts-ignore
      callback(...args);
    };

  useWidgetChanges(qqWidgets, prevQQWidgets, () => setTouchedForms(4));
  useWidgetChanges(textWidgets, prevTextWidgets, () => setTouchedForms(4));

  useEffect(() => {
    if (sponsoredMessage) {
      if (sponsoredMessage.draft || sponsoredMessage.replacementQqPageId) {
        const id =
          sponsoredMessage.replacementQqPageId || sponsoredMessage.qqPageId;
        pageCRUDACtions.retrieveById(id);
        setQQPageId(id);
      }
    }
  }, [sponsoredMessage?.qqPageId, sponsoredMessage?.replacementQqPageId]);

  useEffect(() => {
    if (sponsoredMessage) {
      if (sponsoredMessage.draft || sponsoredMessage.replacementTextPageId) {
        const id =
          sponsoredMessage.replacementTextPageId || sponsoredMessage.textPageId;
        pageCRUDACtions.retrieveById(id);
        setTextPageId(id);
      }
    }
  }, [sponsoredMessage?.textPageId, sponsoredMessage?.replacementTextPageId]);

  const updateFSMContext = (update: Partial<FullPBContext>) =>
    pbSend({
      type: 'CTX_UPDATE_FROM_COMPONENT',
      data: {
        ...update,
      },
    });

  const testBot = () => {
    const page = qqPageSelection() ? qqPage : textPage;
    if (facebookPage && page) {
      window.open(
        `http://m.me/${facebookPage.remoteId}?ref=${page.searchToken}_${session.searchToken}`,
      );
    }
  };

  const currency = symbolForCurrency(
    accountCurrency(
      adAccounts.find(account => account.id === pbState.context.adAccount),
    ),
  );

  const [audiencesRequested, setAudiencesRequested] = React.useState(false);
  const disabled = !pbState.context.sponsoredMessage?.draft;
  const fullDisabled = Boolean(
    pbState.context.sponsoredMessage?.publishingProcess,
  );

  const importStartBot = useCallback(
    wrappedUpdate(
      4,
      async (pageId: number, suggestion: SponsoredMessageModel) => {
        if (!sponsoredMessage || sponsoredMessage.id === suggestion.id) {
          return;
        }
        if (sponsoredMessage.draft) {
          const update = sponsoredMessage.textPageSelected
            ? { textPageId: suggestion.textPageId }
            : { qqPageId: suggestion.qqPageId };
          updateSponsoredMessage(update);
        } else if (pbState.context.chatbotId) {
          const pageId = sponsoredMessage.textPageSelected
            ? suggestion.textPageId
            : suggestion.qqPageId;
          prepareReplacementPage(
            pbState.context.chatbotId,
            sponsoredMessage.id,
            pageId,
          );
        }
      },
    ),
    [sponsoredMessage],
  );

  const onImageUpload = async (assetId: number, url: string) => {
    updateFSMContext({ imageUrl: url });
    updateSponsoredMessage({ assetId });
  };

  const onSelectQQPage = () => {
    updateFSMContext({ textOnly: false });
    updateSponsoredMessage({ textPageSelected: false });
  };

  const onSelectTextPage = () => {
    updateFSMContext({
      textOnly: true,
    });
    updateSponsoredMessage({ textPageSelected: true });
  };

  const qqPageSelection = () => {
    return qqPageId && !pbState.context.textOnly;
  };

  const textPageSelection = () => {
    return textPageId && pbState.context.textOnly;
  };

  const audienceContactsCount = () => {
    return selectedAudience !== undefined
      ? `${selectedAudience?.sponsoredSubscribedContactsCount || 0}`
      : 'N/A';
  };

  const pageWidgets = qqPageSelection() ? qqWidgets : textWidgets;
  const prevPageWidgets = usePrevious({ widgets: pageWidgets });
  const { activateBotGuard } = useInactiveBotActivator();
  const { activateChannelGuard } = useBotChannels();

  useEffect(() => {
    const prevWidgets = (prevPageWidgets || { widgets: [] }).widgets;
    if (!isEqual(pageWidgets, prevWidgets) && prevWidgets.length > 0) {
      updateFSMContext({ updated: true });
    }
  }, [pageWidgets]);

  const adAccountsWithNames = addNamesToAccounts(adAccounts);
  useEffect(() => {
    const ctxAccount = pbState.context.adAccount;
    if (ctxAccount !== undefined && adAccounts.length > 0) {
      const account = adAccounts.find(account => ctxAccount === account.id);
      if (account) {
        assignAdAccount(activePageId, account);
      }
    }
  }, [pbState.context.adAccount, activePageId, adAccounts]);
  useEffect(() => {
    if (sponsoredMessage?.adAccountId && adAccounts.length > 0) {
      const account = adAccounts.find(
        account => sponsoredMessage.adAccountId === account.id,
      );
      updateFSMContext({
        adAccount: account?.id,
      });
    }
  }, [sponsoredMessage?.id, adAccounts]);

  useEffect(() => {
    getRemoteAccounts(activePageId, '');
  }, [activePageId]);

  useEffect(() => {
    if (chatbotId && !audiencesRequested) {
      getAudiences(chatbotId);
      setAudiencesRequested(true);
    }
  }, [chatbotId, audiencesRequested]);

  useEffect(() => {
    const { chatbotId: ctxChatbotId } = pbState.context;
    if (chatbotId !== ctxChatbotId) {
      updateFSMContext({
        chatbotId,
      });
    }
  }, [chatbotId, activePageId]);

  const audiences = useSelector(selectAudiences);
  const parentAudiences = unionBy(audiences, [noneAudience], 'id');

  const selectedAudience = parentAudiences.find(
    aud => aud.id === pbState?.context?.audienceId,
  );

  const isTouchedStep = useCallback(
    (index: number) => {
      return touchedForms >= index + 1;
    },
    [touchedForms],
  );
  const isValid = useCallback(
    (index: number) => {
      return isValidStep<FullPBContext, PBContextKeys>(
        stepFields[index],
        pbState.context.localFieldsErrors,
      );
    },
    [pbState.context.localFieldsErrors],
  );

  const onSuccess = () => {
    pbSend({ type: 'SEND_CLICKED' });
  };

  const onSend = async () => {
    activateBotGuard()
      .then(() => activateChannelGuard(['facebook']))
      .then(onSuccess)
      .catch(() => {}); // handled in the guard
  };

  const configs = [
    {
      header: 'Choose an Ad Account to associate your Sponsored Message with',
      children: (
        <>
          {pbState.context && (
            <>
              <div className="ad-detail__inputs-container">
                <div className="ad-detail__input-container">
                  <div className="ad-detail__field-title">Ad Account</div>
                  <div className="ad-detail__selector">
                    <FilterableSelect
                      onChange={wrappedUpdate(1, async (id: string) => {
                        const account = adAccounts.find(ac => ac.id === id);
                        updateFSMContext({ adAccount: id });
                        updateSponsoredMessage({
                          adAccountId: id,
                          adAccountName: account?.name || '',
                          differentUser: account?.differentUser,
                        });
                      })}
                      outerValue={nameById(
                        sponsoredMessage?.adAccountId || '',
                        adAccountsWithNames.map(account => ({
                          ...account,
                          id: Number(account.id),
                        })),
                        sponsoredMessage?.adAccountId === undefined
                          ? 'No Account Selected'
                          : `${sponsoredMessage.adAccountId} (Access Restricted)`,
                      )}
                      suggestions={generateBasicStructure(adAccountsWithNames)}
                      placeholder="Select Ad Account"
                      disabled={disabled || fullDisabled}
                    />
                    <AccountErrorTooltip
                      classPrefix="ad-detail"
                      hasAccounts={adAccounts.length > 0}
                      validation={pbState.context.localFieldsErrors.adAccount}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      ),
    },
    {
      header: 'Set the targeting for your sponsored message',
      children: (
        <>
          {pbState.context && (
            <>
              <Targeting
                specialAdCategory={false}
                disabled={fullDisabled}
                targeting={targeting}
                remoteAudiences={[]}
                mmAudiences={[]}
                extended={false}
                searchEntity={{
                  type: 'promo_blaster',
                  id: sponsoredMessage?.id,
                }}
                onChangeTargeting={wrappedUpdate(
                  2,
                  (targeting: MMTargeting) => {
                    updateFSMContext({ targeting });
                    updateSponsoredMessage({ targeting });
                  },
                )}
              />
              <hr className="promo-blaster-details__hr" />
              <>
                <div className="ad-detail__inputs-container promo-blaster-details__audience-container">
                  <div className="ad-detail__input-container">
                    <div className="ad-detail__field-title">Audience</div>
                    <div className="ad-detail__selector promo-blaster-details__audience-selector">
                      <AudienceSelector
                        selectedAudienceId={pbState.context.audienceId}
                        disabled={false}
                        fallbackId={-999}
                        onAudienceSelected={wrappedUpdate(
                          2,
                          (audienceId: number) => {
                            updateFSMContext({ audienceId });
                            updateSponsoredMessage({
                              audiences:
                                audienceId === -999 ? [] : [String(audienceId)],
                            });
                          },
                        )}
                        onCancel={wrappedUpdate(2, () => {
                          updateFSMContext({ audienceId: noneAudience.id });
                          updateSponsoredMessage({
                            audiences: [],
                          });
                        })}
                        parentAudiences={parentAudiences}
                      />
                      <div className="promo-blaster-details__audience-contacts-counter">
                        <div className="promo-blaster-details__audience-contacts-counter--text">
                          Reachable Contacts:
                        </div>
                        <div className="promo-blaster-details__audience-contacts-counter--number">
                          {audienceContactsCount()}
                        </div>
                        <TooltipContainer
                          classNameContent="promo-blaster-details__reachable-contacts-tooltip"
                          classNameAnchor="promo-blaster-details__reachable-contacts-anchor"
                          message={
                            <a
                              href="https://customers.ai/help/article/asdve452v7-sponsored-message-reachable-contacts"
                              target="_blank"
                              className="promo-blaster-details__reachable-contacts-link"
                            >
                              About Reachable Contacts
                            </a>
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
                {selectedAudience?.sponsoredSubscribedContactsCount !==
                  undefined &&
                  selectedAudience.sponsoredSubscribedContactsCount <
                    CONTACTS_THRESHOLD && (
                    <Hint className="promo-blaster-details__hint">
                      To avoid risk of under delivery, FB recommends an audience
                      size of at least {CONTACTS_THRESHOLD}
                    </Hint>
                  )}
              </>
            </>
          )}
        </>
      ),
    },
    {
      header: 'Set a Bid & Budget for your audience',
      children: (
        <>
          {pbState.context && (
            <div className="promo-blaster-details__budget-inputs--container">
              <div className="promo-blaster-details__field-title-flex">
                <div>Bid control</div>
                <PrimaryToolTip
                  anchorElement={
                    <span className="promo-blaster-details__question-circle">
                      <i className="far fa-question-circle" />
                    </span>
                  }
                  position="top"
                  align="center"
                >
                  <div>
                    Set the maximum amount Facebook’s algorithm can bid in each
                    auction.
                  </div>
                </PrimaryToolTip>
              </div>
              <div className="ad-detail__inputs-container">
                <div className="ad-detail__input-container">
                  <div className="ad-detail__field-title">Maximum Bid</div>
                  <div className="ad-detail__text-input-container">
                    <div className="ad-detail__text-input-container">
                      <TextInput
                        className="promo-blaster-details__budget-input"
                        placeholder="Enter bid cap"
                        value={`${currency} ${pbState.context.bidAmount / 100}`}
                        onChange={wrappedUpdate(
                          3,
                          (e: React.ChangeEvent<HTMLInputElement>) => {
                            const asNumber = Number(
                              e.target.value.replace(`${currency} `, ''),
                            );
                            if (!isNaN(asNumber)) {
                              const amount = asNumber * 100;
                              updateFSMContext({ bidAmount: amount });
                            }
                          },
                        )}
                        onBlur={() => {
                          // FB has a minimum of $1, which is 100 cents.
                          if (pbState.context.budgetValue < MIN_BID_CAP) {
                            updateFSMContext({ bidAmount: MIN_BID_CAP });
                            updateSponsoredMessage({
                              bidAmount: MIN_BID_CAP,
                            });
                          } else {
                            updateSponsoredMessage({
                              bidAmount: pbState.context.bidAmount,
                            });
                          }
                        }}
                      />
                      <ErrorTooltip
                        classPrefix="ad-detail"
                        validation={pbState.context.localFieldsErrors.bidAmount}
                      />
                    </div>
                    <div className="promo-blaster-details__bid-clarification">
                      <span className="promo-blaster-details__exclamation-circle">
                        <i className="fas fa-exclamation-circle" />
                      </span>
                      FB enforces minimums per country. Check your minimum
                      &nbsp;
                      <a
                        href="https://customers.ai/help/article/am7ckaswnb-promo-blaster-minimum-bids-by-country"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        here.
                      </a>
                      &nbsp; For maximum delivery we recommend a bid of at least
                      $100.
                    </div>
                  </div>
                </div>
              </div>
              <hr className="promo-blaster-details__budget-hr" />
              <div className="promo-blaster-details__field-title">Budget</div>
              <div className="promo-blaster-details__budget-config">
                <div className="promo-blaster-details__budget-selectors">
                  <div className="promo-blaster-details__budget-selector">
                    <div className="ad-detail__field-title">Budget Type</div>
                    <div className="promo-blaster-details__budget-dropdown ad-detail__selector">
                      <SelectorWithArrow
                        selectedItem={budgets.find(
                          budget => budget.id === pbState.context.budgetId,
                        )}
                        disabled={disabled}
                        onChange={wrappedUpdate(3, (id: AdSetBudgetType) => {
                          updateFSMContext({ budgetId: id });
                          updateSponsoredMessage({ adSetBudgetType: id });
                        })}
                        items={budgets}
                      />
                    </div>
                  </div>
                  <div className="promo-blaster-details__budget-selector">
                    <div className="ad-detail__field-title">Budget Value</div>
                    <div className="ad-detail__text-input-container">
                      <div className="ad-detail__text-input-container">
                        <TextInput
                          placeholder="Enter budget"
                          value={`${currency} ${
                            pbState.context.budgetValue / 100
                          }`}
                          onChange={wrappedUpdate(
                            3,
                            (e: React.ChangeEvent<HTMLInputElement>) => {
                              const asNumber = Number(
                                e.target.value.replace(`${currency} `, ''),
                              );
                              if (!isNaN(asNumber)) {
                                updateFSMContext({
                                  budgetValue: asNumber * 100,
                                });
                              }
                            },
                          )}
                          onBlur={() => {
                            if (pbState.context.budgetValue < 100) {
                              updateFSMContext({ budgetValue: 100 });
                              updateSponsoredMessage({ adSetBudget: 100 });
                            } else {
                              updateSponsoredMessage({
                                adSetBudget: pbState.context.budgetValue,
                              });
                            }
                          }}
                        />
                        <ErrorTooltip
                          classPrefix="ad-detail"
                          validation={
                            pbState.context.localFieldsErrors.budgetValue
                          }
                        />
                      </div>
                      <div className="promo-blaster-details__bid-clarification">
                        <span className="promo-blaster-details__exclamation-circle">
                          <i className="fas fa-exclamation-circle" />
                        </span>
                        <span>
                          FB requires the budget to be at least $1 per day.
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      ),
    },
    {
      header: (
        <div className="ad-detail__create-dialogue-header">
          <span>
            Set up sponsored message & bot dialogue{' '}
            <a
              className="ad-detail__test-bot"
              href="#"
              onClick={e => {
                e.stopPropagation();
                testBot();
              }}
            >
              Test my bot
            </a>
          </span>
          <DialogueHeaderWarningAlert alertClassName="ad-detail--warning-alert" />
        </div>
      ),
      renderDropdownOptions:
        nextClicked >= 3 && sponsoredMessage?.draft
          ? () => {
              return (
                <>
                  <DialogueSettingsButton
                    pageId={qqPageSelection() ? qqPageId : textPageId}
                  />
                  <StartBotSponsoredSelector onBotSelect={importStartBot} />
                </>
              );
            }
          : undefined,
      children: (
        <>
          <div className="promo-blaster-detail__image-management-container">
            <div className="ad-detail__field-title">Image:</div>
            <div className="promo-blaster-detail__image-management">
              <div className="promo-blaster-detail__image-management-left">
                <ImageUploader
                  className="promo-blaster-details__image-uploader"
                  needsFileModification={true}
                  file={pbState.context.imageUrl}
                  onUpload={wrappedUpdate(4, onImageUpload)}
                />
              </div>
              <div className="promo-blaster-detail__image-management-right">
                <div className="promo-blaster-detail__image-input-container">
                  <div className="ad-detail__field-title">Image Title</div>
                  <div className="ad-detail__text-input-container">
                    <InputWithEmoji
                      className="promo-blaster-details__image-input"
                      placeholder="Enter a title for your image"
                      value={pbState.context.imageTitle || ''}
                      onChange={wrappedUpdate(4, (value: string) =>
                        updateFSMContext({ imageTitle: value }),
                      )}
                      onBlur={() => {
                        updateSponsoredMessage({
                          title: pbState.context.imageTitle,
                        });
                      }}
                    />
                    <ErrorTooltip
                      classPrefix="ad-detail"
                      validation={pbState.context.localFieldsErrors.imageTitle}
                    />
                  </div>
                </div>
                <div className="promo-blaster-detail__image-input-container">
                  <div className="ad-detail__field-title">
                    Image Subtitle{' '}
                    <span className="promo-blaster-detail__normal-text">
                      (Optional)
                    </span>
                  </div>
                  <InputWithEmoji
                    className="promo-blaster-details__image-input"
                    placeholder="Enter a subtitle to provide more information"
                    value={pbState.context.imageSubTitle || ''}
                    onChange={wrappedUpdate(4, (value: string) =>
                      updateFSMContext({ imageSubTitle: value }),
                    )}
                    onBlur={() => {
                      updateSponsoredMessage({
                        subtitle: pbState.context.imageSubTitle,
                      });
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <hr className="promo-blaster-details__hr" />
          <div className="promo-blaster-details__page-type ad-detail__radio-row">
            <div className="ad-config-detail__image-media ad-detail__radio-container">
              <input
                type="radio"
                id="qq-page-checker"
                className="ad-detail__radio-input"
                disabled={fullDisabled}
                checked={!pbState.context.textOnly}
                onChange={wrappedUpdate(4, onSelectQQPage)}
              />
              <Label className="ad-detail__radio-label" for="qq-page-checker">
                Start with a Quick Question
              </Label>
            </div>
            <div className="ad-config-detail__video-media ad-detail__radio-container">
              <input
                type="radio"
                id="text-page-checker"
                className="ad-detail__radio-input"
                disabled={fullDisabled}
                checked={pbState.context.textOnly}
                onChange={wrappedUpdate(4, onSelectTextPage)}
              />
              <Label className="ad-detail__radio-label" for="text-page-checker">
                Start with some text + buttons
              </Label>
            </div>
          </div>
          {qqPageSelection() && qqPageId && (
            <PathGraphContext.Provider value={qqPathGraph}>
              <StartBotsDialogueEditor
                chatbotId={chatbotId}
                forAds={true}
                widgets={qqWidgets}
                hideTopWidgetAdder={true}
                disableFirstWidgetDeleting={true}
                dialogueId={qqPageId}
                restrictToChannels={['facebook']}
                platforms={['facebook']}
                showOnlyNameAttributes
              />
            </PathGraphContext.Provider>
          )}
          {textPageSelection() && textPageId && (
            <PathGraphContext.Provider value={textPathGraph}>
              <StartBotsDialogueEditor
                chatbotId={chatbotId}
                forAds={true}
                widgets={textWidgets}
                hideTopWidgetAdder={true}
                disableFirstWidgetDeleting={true}
                dialogueId={textPageId}
                restrictToChannels={['facebook']}
                platforms={['facebook']}
                showOnlyNameAttributes
              />
            </PathGraphContext.Provider>
          )}
        </>
      ),
    },
    {
      header: 'Choose when to send this Sponsored Message',
      children: (
        <>
          {pbState.context && (
            <div className="promo-blaster-details__budget-config">
              <div className="promo-blaster-details__budget-selectors">
                <div className="promo-blaster-details__budget-selector">
                  <div className="ad-detail__field-title">Start Date:</div>
                  <div className="promo-blaster-details__date-selector">
                    <Datetime
                      value={pbState.context.startTime.toDate()}
                      onChange={wrappedUpdate(5, (date: Date) => {
                        const startDate = noPastDate(date);
                        const endNoLessThanStartDate = moment.max(
                          pbState.context.endTime,
                          startDate.clone().add(1, 'd'),
                        );
                        updateFSMContext({
                          startTime: startDate,
                          endTime: endNoLessThanStartDate,
                        });
                      })}
                      disabled={
                        sponsoredMessage?.draft === false &&
                        moment(sponsoredMessage?.adSetStartDate).isBefore(
                          new Date(),
                        )
                      }
                      onClose={() =>
                        updateSponsoredMessage({
                          adSetStartDate: pbState.context.startTime.format(
                            'YYYY-MM-DDTHH:mm:ssZ',
                          ),
                          adSetEndDate: pbState.context.endTime.format(
                            'YYYY-MM-DDTHH:mm:ssZ',
                          ),
                        })
                      }
                    />
                  </div>
                </div>
                <div className="promo-blaster-details__budget-selector">
                  <div className="ad-detail__field-title">End Date:</div>
                  <div className="promo-blaster-details__date-selector">
                    <Datetime
                      value={pbState.context.endTime.toDate()}
                      onChange={wrappedUpdate(5, (date: Date) => {
                        const endDate = noPastDate(date);
                        const noLessThanStartDate = moment.max(
                          pbState.context.startTime.clone().add(1, 'd'),
                          endDate,
                        );
                        updateFSMContext({ endTime: noLessThanStartDate });
                      })}
                      onClose={() =>
                        updateSponsoredMessage(
                          {
                            adSetEndDate: pbState.context.endTime.format(
                              'YYYY-MM-DDTHH:mm:ssZ',
                            ),
                          },
                          'adSetEndDate',
                        )
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="promo-blaster-details__buttons">
            <Button
              onClick={onSend}
              disabled={!allowPublishing}
              className="promo-blaster-details__button"
              extraPadding
            >
              {sponsoredMessage?.draft === false ? 'Save' : 'Send'}
            </Button>
            <Button
              onClick={() => pbSend({ type: 'CANCEL_CLICKED' })}
              red
              invert
              transparent
              extraPadding
              className="promo-blaster-details__button"
            >
              Cancel
            </Button>
          </div>
        </>
      ),
    },
  ];
  const [stepsStatus, setStepsStatus] = useFormSteps(
    configs,
    isCollapsed,
    nextClicked,
  );

  const formSteps = formStepContainerBuilder({
    configs,
    options: {
      className: 'ad-detail__form-step',
      contentClassName: 'ad-detail__form-step-collapsible-content',
      isCollapsed: (index: number) => {
        if (index === 0) {
          return false;
        }
        return nextClicked < index;
      },
      isTouched: isTouchedStep,
      isValid,
      nextAllowed: (index: number) => isValid(index) || nextClicked > index,
      onNext: (index: number, allowed?: boolean) => {
        setTouchedForms(Math.max(touchedForms, index + 1));
        if (allowed) {
          setNextClicked(Math.max(touchedForms, index + 1));
          if (
            sponsoredMessage?.draft &&
            (sponsoredMessage?.progressStep === null ||
              sponsoredMessage?.progressStep <= index)
          ) {
            updateSponsoredMessage({ progressStep: index + 1 });
          }
        }
      },
      stepsStatus,
      setStepsStatus,
    },
  });

  if (!pbState) return <></>;

  return (
    <div className="ad-detail-container">
      <EditPromoBlasterHeader
        sponsoredMessage={sponsoredMessage}
        showPublish={true}
        onSaveClick={onSend}
        canPublish={Boolean(allowPublishing && canPublish(pbState.context))}
        onSaveName={(name: string) => {
          updateFSMContext({ promoBlasterName: name });
          updateSponsoredMessage({ name }, 'name');
        }}
        errors={pbState.context.localFieldsErrors.promoBlasterName}
        isTemplateBot={!!activeBot.template}
      />
      <InactiveBotAlert />
      <Alert
        sponsoredMessage={sponsoredMessage}
        isFree={activeFacebookPagePlanInfo.isFreeOrSpecialPlan}
      />
      <November2020EuroAdsChangesAlert />
      <div className="ad-detail__steps-container">{formSteps}</div>
    </div>
  );
};

const enhance = compose<InnerProps, OuterProps>(
  connect(mapStateToProps, mapDispatchToProps),
);

export const PromoBlasterDetails = enhance(PromoBlasterDetailPanelBase);
